// Pure helpers behind the text editor in PlacementSection: the patch the
// "Just text" tile writes, and the patch typing writes. Extracted so the
// mode-switching rules can be unit tested without the DOM (see
// textDraft.test.ts); PlacementSection only wraps these in `set()`.
//
// Audit 2026-10 (high): on single-design templates (pet / kid / person)
// typing on a TEXT-ONLY additional spot flipped the draft to mode "both"
// and demanded an upload - a dead end for the customer who picked "Just
// text". The promote-to-"both" rule exists for the DESIGN draft (type a
// name under the drawing), so it now only runs there; a text-only draft
// (the picker's "Just text" tile, the sleeve cross-sell's name draft)
// just takes the text.

import type { Arrangement, EmbroideryDraft } from "./sections/PlacementSection";

/** The patch `pickTextOnly` applies: text mode with every artwork and
 *  reuse marker scrubbed, so nothing from a previous design-mode pick
 *  can leak onto a text spot (stale upload-later holds, bogus "reused
 *  from" properties, waived fees). */
export function textOnlyPatch(): Partial<EmbroideryDraft> {
  return {
    mode: "text",
    addonUpsellId: null,
    addonCount: null,
    addonStyleColoured: false,
    uploadStagingKey: null,
    uploadFilename: null,
    uploadMimeType: null,
    uploadFileHash: null,
    // Text mode has no artwork - a stale "send it later" flag here
    // would tag + hold the order for an upload that can never happen.
    uploadLater: false,
    // 2026-08-21: same hygiene for the reuse markers. A stale
    // already-digitised flag on a text spot emits a bogus "reused
    // from" property, and if the customer later flips back to a logo
    // flow it silently waives a fee that should charge.
    uploadAlreadyDigitised: false,
    sourceOrderName: null,
    reusedFromSpot: null,
    extraUploads: [],
  };
}

export type TextInputRules = {
  /** Sleeve / arm spots and maxLines=1 templates: newlines become spaces. */
  singleLineOnly: boolean;
  /** Hard cap on rows (template maxLines, min 1). */
  maxLines: number;
  /** Per-row character budget (template maxCharsPerLine). */
  perLineMax: number;
  /** Caps-only fonts / Word Art: the stitched face is uppercase. */
  forceUpper: boolean;
};

/** Normalise raw input to what will be stitched: CRLF -> LF, line cap,
 *  per-line clip, optional uppercase. */
export function normaliseTextInput(raw: string, rules: TextInputRules): string {
  let value = raw.replace(/\r\n?/g, "\n");
  if (rules.singleLineOnly) value = value.replace(/\n+/g, " ");
  const lines = value.split("\n").slice(0, Math.max(1, rules.maxLines));
  const clipped = lines
    .map((ln) =>
      ln.length > rules.perLineMax ? ln.slice(0, rules.perLineMax) : ln,
    )
    .join("\n");
  return rules.forceUpper ? clipped.toUpperCase() : clipped;
}

export type TextChangeFlags = TextInputRules & {
  /** Pet / kid / person template. */
  isSingleDesignFlow: boolean;
  /** Template demands a name (Phase 67d), unless the placement bypasses locks. */
  textRequired: boolean;
  /** Arrangement a single-design draft locks to when text lands. */
  lockedArrangement: Arrangement;
};

/** The patch typing applies to `draft`.
 *
 *  Single-design flows promote the DESIGN draft between "logo" (no text)
 *  and "both" (name under the drawing) as the text comes and goes; a
 *  textRequired template stays on "both" even while empty so the input
 *  keeps rendering. A text-only draft (mode "text") never promotes: it
 *  has no artwork to pair the name with, so it just takes the text. The
 *  logo flow likewise only ever writes the text. */
export function textChangePatch(
  draft: Pick<EmbroideryDraft, "mode">,
  raw: string,
  flags: TextChangeFlags,
): Partial<EmbroideryDraft> {
  const next = normaliseTextInput(raw, flags);
  if (!flags.isSingleDesignFlow || draft.mode === "text") {
    return { text: next };
  }
  if (next.trim().length > 0 || flags.textRequired) {
    return { text: next, mode: "both", arrangement: flags.lockedArrangement };
  }
  return { text: next, mode: "logo", arrangement: null };
}
